import { buildDayFilterOptions, formatTimeRange, groupAppointments } from '@ramp106/omrjs-events-ui'
import { eachDayOfInterval, format, parseISO } from 'date-fns'
import { computed, inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { ROOT_EVENT_SLUG } from '@/components/VisitorProfiles/config'
import {
  type AppointmentStateEnum,
  useEventBySlugQuery,
  useMeetingsAppointmentsQuery,
  useMeetingsUpdateExhibitorNotesMutation,
  type MeetingsAppointmentsQuery,
} from '@/gql/myomr'

export type AppointmentNode = NonNullable<NonNullable<MeetingsAppointmentsQuery['meetings']>['appointments']>['nodes'][number]

export function useMeetingsBase(states: AppointmentStateEnum[]) {
  const { t } = useI18n()
  const refetchCounters = inject<() => void>('refetchCounters', () => {})
  const showToast = inject<(message: string, type: 'success' | 'error') => void>('showToast', () => {})

  // State
  const selectedDay = ref<string>('')

  // GraphQL queries
  const { result: eventResult, loading: eventLoading } = useEventBySlugQuery({
    slug: ROOT_EVENT_SLUG,
  })

  const {
    result,
    loading: appointmentsLoading,
    refetch,
  } = useMeetingsAppointmentsQuery(
    () => ({
      eventSlug: ROOT_EVENT_SLUG,
      states,
      day: selectedDay.value || undefined,
    }),
    () => ({
      fetchPolicy: 'cache-and-network',
    }),
  )

  const { mutate: updateExhibitorNotes } = useMeetingsUpdateExhibitorNotesMutation()

  // Loading state
  const loading = computed(() => (appointmentsLoading.value || eventLoading.value) && !result.value)

  // Event days for filter dropdown
  const eventDays = computed(() => {
    const event = eventResult.value?.eventBySlug
    if (!event?.startsAt || !event?.endsAt) return []

    const start = parseISO(event.startsAt)
    const end = parseISO(event.endsAt)

    return eachDayOfInterval({ start, end }).map((day) => format(day, 'yyyy-MM-dd'))
  })

  // Day filter options for SingleSelect
  const dayFilterOptions = computed(() =>
    buildDayFilterOptions(eventDays.value, t('Meetings.myMeetings.allDays'), (date, index) =>
      t('Meetings.myMeetings.dayOption', {
        date: formatDateShort(date),
        dayNumber: index + 1,
      }),
    ),
  )

  // Appointments from query
  const appointments = computed<AppointmentNode[]>(() => {
    return result.value?.meetings?.appointments?.nodes ?? []
  })

  // Total count for tab badge
  const totalCount = computed(() => {
    return result.value?.meetings?.appointments?.pageInfo?.totalCount ?? 0
  })

  // Group appointments by calendar availability
  const groupedAppointments = computed(() =>
    groupAppointments(appointments.value, {
      getGroupKey: (a) => a.calendarAvailability?.id ?? a.startsAt,
      getLabel: (items) => {
        const first = items[0]
        const avail = first.calendarAvailability
        const startsAt = avail?.startsAt ?? first.startsAt
        const endsAt = avail?.endsAt ?? first.endsAt
        const d = new Date(startsAt)
        return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${formatTimeRange(startsAt, endsAt)}`
      },
      getSortKey: (a) => a.calendarAvailability?.startsAt ?? a.startsAt,
    }),
  )

  // Helper functions
  function formatDateShort(dateStr: string): string {
    const date = parseISO(dateStr)
    return format(date, 'MMM d')
  }

  async function saveExhibitorNotes(appointmentId: string, notes: string): Promise<boolean> {
    try {
      const result = await updateExhibitorNotes({
        calendarAppointmentId: appointmentId,
        exhibitorNotes: notes || null,
      })

      if (result?.data?.meetingsUpdateAppointmentStatus?.errors?.length) {
        result.data.meetingsUpdateAppointmentStatus.errors.forEach((error) => {
          if (error) showToast(error, 'error')
        })
        return false
      }

      showToast(t('Meetings.myMeetings.notesSaved'), 'success')
      await refetch()
      return true
    } catch (_error) {
      showToast(t('common.errorOccurred'), 'error')
      return false
    }
  }

  return {
    selectedDay,
    loading,
    appointments,
    groupedAppointments,
    totalCount,
    dayFilterOptions,
    saveExhibitorNotes,
    refetch,
    refetchCounters,
    showToast,
  }
}
